import {
  IonHeader,
  IonToolbar,
  IonButton,
  IonContent,
  IonItem,
  IonIcon,
  IonText,
  IonList,
  IonItemGroup,
  IonFab,
  IonFabButton,
  IonPage,
  useIonModal,
  IonTextarea,
} from "@ionic/react";
import React, { useRef, useState } from "react";
import { closeCircleOutline, searchCircle } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { Category, FilterProps, Grouping } from "../../models/Data";
import SelectOption from "../input/SelectOption";
import "./Filter.scss";

const Filter: React.FC<FilterProps> = ({
  items,
  selectedItems,
  onFilterCancel,
  onFilterChange,
}) => {
  const modalFilterItem = useRef<HTMLIonModalElement>(null);
  const { t } = useTranslation(["general"]);
  const [selectCategory, setSelectCategory] =useState<Category[]>(selectedItems.categories);
  const [selectGrouping, setSelectGrouping] = useState<Grouping[]>(selectedItems.groupings);
  const clearFilters = () => {
    setSelectCategory([]);
    setSelectGrouping([]);
  };

  const [presentCategoryModal, dismissCategoryModal] = useIonModal(
    SelectOption,
    {
      title: `${t("category")}`,
      items: items.categories.map((value: Category) => ({
        value: value.category_id,
        text: `${value.category_name}`,
      })),
      selectedItems: selectCategory.length
        ? selectCategory.map((c: Category) => c.category_id)
        : [],
      onSelectionCancel: () => dismissCategoryModal(),
      onSelectionChange: (selected: string[]) => {
        const listCategory = selected.reduce(
          (acc: Category[], idCategory: string) => {
            const foundCategory = items.categories.find(
              (c: Category) => c.category_id === idCategory
            );
            if (foundCategory) {
              acc.push({
                category_id: foundCategory.category_id,
                category_name: foundCategory.category_name,
              });
            }
            return acc;
          },
          []
        );
        setSelectCategory(listCategory);
        dismissCategoryModal();
      },
      multiple: true,
    }
  );

  const [presentGroupingModal, dismissGroupingModal] = useIonModal(
    SelectOption,
    {
      title: `${t("grouping")}`,
      items: items.groupings.map((value: Grouping) => ({
        value: value.grouping_id,
        text: `${value.grouping_name}`,
      })),
      selectedItems: selectGrouping.length
        ? selectGrouping.map((g: Grouping) => g.grouping_id)
        : [],
      onSelectionCancel: () => dismissGroupingModal(),
      onSelectionChange: (selected: string[]) => {
        const listGrouping = selected.reduce(
          (acc: Grouping[], idGrouping: string) => {
            const foundGrouping = items.groupings.find(
              (g: Grouping) => g.grouping_id === idGrouping
            );
            if (foundGrouping) {
              acc.push({
                grouping_id: foundGrouping.grouping_id,
                grouping_name: foundGrouping.grouping_name,
              });
            }
            return acc;
          },
          []
        );
        setSelectGrouping(listGrouping);
        dismissGroupingModal();
      },
      multiple: true,
    }
  );

  const filterItem = () => {
    onFilterChange({
      categories: selectCategory,
      groupings: selectGrouping,
    });
  };

  return (
    <IonPage ref={modalFilterItem}>
      <IonHeader>
        <IonToolbar>
          <IonButton
            color="light"
            slot="start"
            fill="clear"
            onClick={() => {
              onFilterCancel();
            }}
          >
            <IonIcon
              className="ion-no-padding ion-no-margin"
              color="light"
              slot="start"
              src={closeCircleOutline}
            />
          </IonButton>
          <IonText color="light" slot="start" className="ion-padding-start">
            <h4>{t("filter")}</h4>
          </IonText>
          {selectCategory.length || selectGrouping.length ? (
            <IonButton slot="end" fill="clear" onClick={clearFilters}>
              <IonText color="danger">Reset</IonText>
            </IonButton>
          ) : (
            ""
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding-start">
        <IonFab slot="fixed" vertical="top" horizontal="end">
          <IonFabButton color="danger" onClick={filterItem}>
            <IonIcon size="large" icon={searchCircle}></IonIcon>
          </IonFabButton>
        </IonFab>
        <IonList className="ion-no-padding ion-no-margin">
          <IonItemGroup>
            <IonItem className="ion-no-padding ion-no-margin">
              <IonTextarea
                labelPlacement="floating"
                placeholder={"-"}
                label={`${t("select")} ${t("category")}`}
                readonly={true}
                value={selectCategory
                  ?.map((category) => category.category_name)
                  .join(", ")}
                onClick={() => {
                  presentCategoryModal({
                    initialBreakpoint: 0.9,
                    breakpoints: [0, 0.9, 1.0],
                    handleBehavior: "cycle",
                    backdropDismiss: true,
                    id: "modal-category",
                    onWillDismiss: async () => {
                      const modalElement = document.getElementById(
                        "modal-category"
                      ) as HTMLIonModalElement;
                      if (modalElement) {
                        modalElement?.dismiss();
                      }
                    },
                  });
                }}
              ></IonTextarea>
            </IonItem>
            <IonItem className="ion-no-padding ion-no-margin">
              <IonTextarea
                labelPlacement="floating"
                placeholder={"-"}
                label={`${t("select")} ${t("category")}`}
                readonly={true}
                value={selectGrouping
                  ?.map((grouping) => grouping.grouping_name)
                  .join(", ")}
                onClick={() => {
                  presentGroupingModal({
                    initialBreakpoint: 0.9,
                    breakpoints: [0, 0.9, 1.0],
                    handleBehavior: "cycle",
                    backdropDismiss: true,
                    id: "modal-grouping",
                    onWillDismiss: async () => {
                      const modalElement = document.getElementById(
                        "modal-grouping"
                      ) as HTMLIonModalElement;
                      if (modalElement) {
                        modalElement?.dismiss();
                      }
                    },
                  });
                }}
              ></IonTextarea>
            </IonItem>
          </IonItemGroup>
        </IonList>
      </IonContent>
    </IonPage>
  );
};

export default Filter;
